'use client';

import { useRef, useEffect } from 'react';
import { usePathname } from 'next/navigation';

type UsePathnameReturnType = ReturnType<typeof usePathname>;

type Callback = (pathName: UsePathnameReturnType) => void

/**
 * Runs a callback function when the page path changes. Uses Nextjs usePathname hook. Created this as we can't
 * use next/router events, see link below.
 * @see https://nextjs.org/docs/app/api-reference/functions/use-pathname
 * @see https://nextjs.org/docs/messages/next-router-not-mounted
 */
const useOnNavigate = (callback: Callback) => {
    const pathName = usePathname();

    // This ref is used to compare the previous path with a new path to ensure it has changed.
    const currentUrlPath = useRef(pathName);

    useEffect(() => {
        const newUrlPath = pathName;

        // This "if" allows us to skip the first render and makes sure the callback is only called when the
        // path changes.
        if (currentUrlPath.current === newUrlPath) {
            return;
        }

        currentUrlPath.current = newUrlPath;
        callback(newUrlPath);
    }, [pathName, callback]);
};

export default useOnNavigate;
